import { useState } from 'react';
import { useT } from '@/store';
import { VEHICLE_TYPES } from '@/hooks/useFleet';
import {
  useDeleteDwellRateTier, useDwellRateTiers, useSaveDwellRateTier,
} from '@/hooks/useDataAdmin';
import { EmptyState, ErrorState, SkeletonRows } from '@/components/ui/Panel';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { parseDecimalInput } from '@/lib/format';

/**
 * Transporto kainodaros modeliai — prastovos valandinio įkainio
 * diferenciacija pagal transporto tipą/talpą. Be tiers čia,
 * core.dwell_engine.resolve_dwell_hourly_rate() grįžta prie plokščio
 * tenants.dwell_hourly_rate_eur (TenantSettings) — tai tas pats esamas
 * elgesys, kurį šis ekranas papildo, ne pakeičia.
 */
export function DwellRateTiersList() {
  const t = useT();
  const { data: tiers = [], isLoading } = useDwellRateTiers();
  const saveTier = useSaveDwellRateTier();
  const deleteTier = useDeleteDwellRateTier();
  const [formError, setFormError] = useState(null);

  const [vehicleType, setVehicleType] = useState('');
  const [minCapacity, setMinCapacity] = useState('');
  const [maxCapacity, setMaxCapacity] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');

  const add = () => {
    const rate = parseDecimalInput(hourlyRate);
    if (Number.isNaN(rate)) return setFormError('dwellRates.errHourlyRate');
    setFormError(null);
    const min = parseDecimalInput(minCapacity);
    const max = parseDecimalInput(maxCapacity);
    saveTier.mutate(
      {
        vehicle_type: vehicleType || null,
        ...(Number.isFinite(min) ? { min_capacity_tons: min } : null),
        ...(Number.isFinite(max) ? { max_capacity_tons: max } : null),
        hourly_rate_eur: rate,
      },
      {
        onSuccess: () => {
          setVehicleType('');
          setMinCapacity('');
          setMaxCapacity('');
          setHourlyRate('');
        },
      },
    );
  };

  return (
    <div className="space-y-2">
      <h4 className="panel-title !mb-1">{t('dwellRates.heading')}</h4>
      <p className="text-[10.5px] leading-snug text-dim">{t('dwellRates.hint')}</p>

      {isLoading ? (
        <SkeletonRows rows={2} />
      ) : tiers.length === 0 ? (
        <EmptyState>{t('dwellRates.empty')}</EmptyState>
      ) : (
        <ul className="space-y-1">
          {tiers.map((tier) => (
            <li key={tier.tier_id} className="flex items-center justify-between gap-2 text-[12px]">
              <span className="min-w-0 flex-1 truncate">
                {tier.vehicle_type ? t(`transportas.type.${tier.vehicle_type}`) : t('dwellRates.allTypes')}
                {tier.min_capacity_tons != null || tier.max_capacity_tons != null
                  ? ` (${tier.min_capacity_tons ?? '…'}–${tier.max_capacity_tons ?? '…'} t)`
                  : ''}
              </span>
              <input
                className="field !w-20 !py-1 !text-[11px] text-right"
                defaultValue={tier.hourly_rate_eur}
                inputMode="decimal"
                onBlur={(e) => {
                  const value = parseDecimalInput(e.target.value);
                  if (Number.isNaN(value) || value === tier.hourly_rate_eur) return;
                  saveTier.mutate({ tierId: tier.tier_id, hourly_rate_eur: value });
                }}
              />
              <span className="text-dim">{t('dwellRates.hourUnit')}</span>
              <ConfirmButton
                label="✕"
                confirmLabel={t('common.confirmDelete')}
                onConfirm={() => deleteTier.mutate(tier.tier_id)}
              />
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <select
          className="field !w-auto flex-1 !py-1 !text-[11px]"
          value={vehicleType}
          onChange={(e) => setVehicleType(e.target.value)}
        >
          <option value="">{t('dwellRates.allTypes')}</option>
          {VEHICLE_TYPES.map((code) => (
            <option key={code} value={code}>{t(`transportas.type.${code}`)}</option>
          ))}
        </select>
        <input
          className="field !w-20 !py-1 !text-[11px]"
          value={minCapacity}
          onChange={(e) => setMinCapacity(e.target.value)}
          placeholder={t('dwellRates.minCapacityPh')}
          inputMode="decimal"
        />
        <input
          className="field !w-20 !py-1 !text-[11px]"
          value={maxCapacity}
          onChange={(e) => setMaxCapacity(e.target.value)}
          placeholder={t('dwellRates.maxCapacityPh')}
          inputMode="decimal"
        />
        <input
          className="field !w-24 !py-1 !text-[11px]"
          value={hourlyRate}
          onChange={(e) => setHourlyRate(e.target.value)}
          placeholder={t('dwellRates.hourlyRatePh')}
          inputMode="decimal"
        />
        <button type="button" className="btn" onClick={add} disabled={saveTier.isPending}>
          {t('common.addBtn')}
        </button>
      </div>

      {formError ? <ErrorState error={new Error(t(formError))} /> : null}
      {saveTier.isError ? <ErrorState error={saveTier.error} /> : null}
      {deleteTier.isError ? <ErrorState error={deleteTier.error} /> : null}
    </div>
  );
}
